<template>
  <!-- Panneau de restitution posé dans la `.cards-grid` des dashboards du personnel.
       Il donne enfin un écran de lecture à l'agrégat `mealFeedbackStats/`, jusqu'ici
       appelé par une action Vuex que rien ne montait (le code mort de la spec). -->
  <section class="meal-feedback-stats" aria-label="Satisfaction repas">
    <div class="stats-card">
      <span class="card-header">
        <span class="card-title">🍽️ Satisfaction repas</span>
        <span v-if="!isLoading && !hasError && stats" class="badge badge-blue">{{ feedbackCountLabel }}</span>
      </span>

      <p v-if="isLoading" class="stats-state" role="status">Chargement de la satisfaction…</p>

      <p v-else-if="hasError" class="stats-state" role="alert">
        La satisfaction repas n'a pas pu être chargée. Réessayez dans un instant.
      </p>

      <!-- État vide explicite : aucune passation, pas une moyenne à 0 (RGAA + spec S54). -->
      <p v-else-if="stats && stats.totalFeedbacks === 0" class="stats-state stats-empty">
        Aucun retour repas pour l'instant.
      </p>

      <template v-else-if="stats">
        <ul class="stats-list">
          <li class="stats-row">
            <span class="stats-label">Convives</span>
            <template v-if="consumerAverage !== null">
              <span class="stats-value">{{ formatRating(consumerAverage) }} / 5</span>
              <span class="stats-count">{{ respondentsLabel(stats.consumerSatisfaction.count) }}</span>
            </template>
            <span v-else class="stats-count stats-count--empty">Aucun avis convive</span>
          </li>

          <li class="stats-row">
            <span class="stats-label">Adultes</span>
            <template v-if="staffAverage !== null">
              <span class="stats-value">{{ formatRating(staffAverage) }} / 5</span>
              <span class="stats-count">{{ respondentsLabel(stats.staffSatisfaction.count) }}</span>
            </template>
            <span v-else class="stats-count stats-count--empty">Aucun avis adulte</span>
          </li>
        </ul>

        <!-- L'écart n'est annoncé que si les deux populations ont répondu (spec S54).
             Le sens est porté par le texte, la couleur ne fait que le redoubler (RGAA 3.1). -->
        <p v-if="gap !== null" class="stats-gap">
          <span class="badge" :class="gapClass">{{ gapLabel }}</span>
          <span class="stats-gap-detail">{{ gapDetail }}</span>
        </p>
      </template>
    </div>
  </section>
</template>

<script>
import Constants from "@/constants"

/**
 * Restitution de l'agrégat de satisfaction repas d'une cantine sur les dashboards
 * du personnel (MANAGER / SUPERVISOR / WORKER).
 *
 * Le panneau ne choisit pas la cantine : elle lui est passée par le dashboard hôte,
 * qui la tient de `DashboardContextHeader`. La lecture passe par une action Vuex, et
 * jamais un `fetch` direct dans le composant.
 *
 * L'agrégat est d'établissement : deux moyennes distinctes (convives, adultes) et leur
 * écart, jamais une moyenne mêlée, jamais une réponse individuelle.
 */
export default {
  name: "MealFeedbackStatsPanel",

  props: {
    canteenId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      stats: null,
      status: Constants.LoadingStatus.IDLE,
    }
  },

  computed: {
    isLoading() {
      return this.status === Constants.LoadingStatus.LOADING
    },
    hasError() {
      return this.status === Constants.LoadingStatus.ERROR
    },
    consumerAverage() {
      return this.stats && this.stats.consumerSatisfaction ? this.stats.consumerSatisfaction.averageRating : null
    },
    staffAverage() {
      return this.stats && this.stats.staffSatisfaction ? this.stats.staffSatisfaction.averageRating : null
    },
    gap() {
      return this.stats && this.stats.ratingGap !== undefined ? this.stats.ratingGap : null
    },
    feedbackCountLabel() {
      const total = (this.stats && this.stats.totalFeedbacks) || 0
      return `${total} avis`
    },
    // Convention de signe de l'endpoint : positif quand les adultes notent au-dessus des convives.
    gapLabel() {
      if (this.gap > 0) return "Adultes plus satisfaits"
      if (this.gap < 0) return "Convives plus satisfaits"
      return "Satisfaction alignée"
    },
    gapClass() {
      if (this.gap > 0) return "badge-blue"
      if (this.gap < 0) return "badge-amber"
      return "badge-grey"
    },
    gapDetail() {
      const abs = Math.abs(this.gap)
      if (abs === 0) return "Convives et adultes notent au même niveau."
      return `Écart de ${this.formatRating(abs)} point${abs > 1 ? "s" : ""} sur 5.`
    },
  },

  watch: {
    canteenId: "load",
  },

  mounted() {
    this.load()
  },

  methods: {
    formatRating(value) {
      if (value === null || value === undefined) return "—"
      return Number(value)
        .toFixed(1)
        .replace(".", ",")
    },
    respondentsLabel(count) {
      return `${count} avis`
    },
    load() {
      if (!this.canteenId) {
        this.stats = null
        this.status = Constants.LoadingStatus.IDLE
        return
      }
      this.status = Constants.LoadingStatus.LOADING
      // `Promise.resolve` protège d'un store qui ne renverrait pas de promesse
      // (cas d'un dashboard hôte dont le mock de test ne déclare pas l'action).
      return Promise.resolve(this.$store.dispatch("fetchMealFeedbackStats", { canteenId: this.canteenId }))
        .then((response) => {
          this.stats = response
          this.status = Constants.LoadingStatus.SUCCESS
        })
        .catch(() => {
          this.status = Constants.LoadingStatus.ERROR
        })
    },
  },
}
</script>

<style scoped>
/* Le panneau occupe une cellule de la `.cards-grid` : sa card en prend toute la hauteur
   pour s'aligner sur ses voisines. */
.meal-feedback-stats {
  height: 100%;
}

/* Valeurs reprises de `.card` des dashboards hôtes (styles scoped, non héritables ici),
   à l'image de la card sœur MealFeedbackPanel. */
.stats-card {
  display: block;
  width: 100%;
  height: 100%;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.stats-state {
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0;
}

.stats-empty {
  font-style: italic;
}

.stats-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
}

.stats-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.stats-row:last-child {
  border-bottom: none;
}

.stats-label {
  flex: 0 0 5.5rem;
  font-weight: 600;
  color: #1a1a1a;
}

.stats-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stats-count {
  font-size: 0.85rem;
  color: #6b7280;
}

.stats-count--empty {
  font-style: italic;
}

.stats-gap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0;
}

.stats-gap-detail {
  font-size: 0.85rem;
  color: #4b5563;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}

.badge-amber {
  background: #fef3c7;
  color: #92400e;
}

.badge-grey {
  background: #f3f4f6;
  color: #4b5563;
}
</style>
